import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Search, RefreshCw, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import api, { formatApiError, formatRelative } from "../../lib/api";
import Layout from "../../components/Layout";

const KIND_LABEL = {
  password_reset: "Passord-reset",
  email_verification: "Bekreft e-post",
  trial_ending: "Prøveperiode",
  wheel_winner: "Lykkehjul-vinst",
  welcome: "Velkommen",
  purchase_receipt: "Kvittering",
};

export default function AdminEpostLog() {
  const [email, setEmail] = useState("");
  const [kind, setKind] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [msg, setMsg] = useState(null);

  const search = async () => {
    setLoading(true); setMsg(null);
    try {
      const params = new URLSearchParams();
      if (email.trim()) params.set("email", email.trim());
      if (kind) params.set("kind", kind);
      params.set("limit", "50");
      const r = await api.get(`/admin/email-log?${params}`);
      setData(r.data);
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  };

  const resendReset = async () => {
    if (!email.trim()) return;
    if (!window.confirm(`Sende ny passord-reset-lenke til ${email}?\n\nDette ignorerer rate-limit og logges i audit-loggen.`)) return;
    setResending(true); setMsg(null);
    try {
      const r = await api.post(`/admin/email-log/resend-password-reset?email=${encodeURIComponent(email.trim())}`);
      setMsg({ type: "ok", text: `Sendt til ${r.data.sent_to} (email_id: ${r.data.email_id})` });
      await search();
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e) });
    } finally {
      setResending(false);
    }
  };

  const rs = data?.reset_status;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/admin/board" className="text-[var(--cream-soft)] hover:text-[var(--magenta)]" data-testid="epost-log-back">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="font-fraunces italic text-2xl text-[var(--cream)] flex items-center gap-2">
            <Mail className="h-6 w-6 text-[var(--magenta)]" />
            E-post-log
          </h1>
        </div>

        <p className="text-sm text-[var(--cream-soft)]">
          Diagnose for utsendte transaksjons-e-poster. Vis Resend-status per mottaker og send på nytt hvis nødvendig.
        </p>

        <div className="border border-white/10 bg-[var(--noir-elev)] p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[2fr,1fr,auto] gap-2">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && search()}
              placeholder="bruker@epost.no"
              className="input-field !text-black placeholder:!text-gray-500"
              data-testid="epost-log-email-input"
            />
            <select
              value={kind}
              onChange={e => setKind(e.target.value)}
              className="input-field !text-black"
              data-testid="epost-log-kind-select"
            >
              <option value="">Alle typer</option>
              <option value="password_reset">Passord-reset</option>
              <option value="email_verification">Bekreft e-post</option>
              <option value="trial_ending">Prøveperiode</option>
              <option value="wheel_winner">Lykkehjul-vinst</option>
              <option value="welcome">Velkommen</option>
              <option value="purchase_receipt">Kvittering</option>
            </select>
            <button
              onClick={search}
              disabled={loading}
              className="btn-magenta flex items-center gap-2 justify-center"
              data-testid="epost-log-search-btn"
            >
              {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Søk
            </button>
          </div>

          {msg && (
            <div className={`text-xs p-2 border ${msg.type === "ok" ? "border-green-500/40 text-green-300" : "border-red-500/40 text-red-300"}`} data-testid="epost-log-msg">
              {msg.text}
            </div>
          )}
        </div>

        {rs && (
          <div className="border border-[var(--magenta)]/40 bg-[var(--magenta)]/5 p-4 space-y-2" data-testid="epost-log-reset-status">
            <h3 className="font-fraunces italic text-lg text-[var(--cream)]">Passord-reset-status for {email}</h3>
            <div className="text-xs space-y-1 text-[var(--cream-soft)]">
              <div>
                Bruker eksisterer:{" "}
                {rs.user_exists ? (
                  <span className="text-green-400">Ja (@{rs.username})</span>
                ) : (
                  <span className="text-red-400">Nei — e-post finnes ikke i databasen</span>
                )}
              </div>
              {rs.user_deleted && (
                <div className="text-red-400 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Konto er slettet
                </div>
              )}
              <div>Reset-forsøk siste 15 min: <span className="font-bold text-[var(--cream)]">{rs.resets_last_15_min}</span></div>
              {rs.rate_limited && (
                <div className="text-orange-400 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Rate-limited (3+ forsøk siste 15 min — nye forsøk droppes stille)
                </div>
              )}
            </div>
            {rs.user_exists && !rs.user_deleted && (
              <button
                onClick={resendReset}
                disabled={resending}
                className="btn-magenta text-xs mt-2 inline-flex items-center gap-2"
                data-testid="epost-log-resend-btn"
              >
                {resending ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                Tvunget send av reset-lenke (bypasser rate-limit)
              </button>
            )}
          </div>
        )}

        {data && (
          <div className="space-y-2" data-testid="epost-log-results">
            <div className="text-xs text-[var(--cream-faint)]">
              {data.count} treff (viser siste 50)
            </div>
            {data.items.length === 0 && (
              <div className="text-sm text-[var(--cream-soft)] italic p-4 border border-white/5">
                Ingen e-poster funnet for dette søket. Betyr enten at ingen har blitt sendt, eller at mottakeren du søker på ikke er brukt.
              </div>
            )}
            {data.items.map((it, i) => (
              <div key={i} className="border border-white/10 p-3 bg-[var(--noir-elev)]/50" data-testid={`epost-log-item-${i}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--magenta)]">
                    {KIND_LABEL[it.kind] || it.kind}
                  </div>
                  <div className="text-[10px] text-[var(--cream-faint)]">{formatRelative(it.sent_at)}</div>
                </div>
                <div className="text-xs text-[var(--cream)] mb-1"><span className="text-[var(--cream-faint)]">Til:</span> {it.to}</div>
                <div className="text-xs text-[var(--cream-soft)] mb-1"><span className="text-[var(--cream-faint)]">Emne:</span> {it.subject}</div>
                <div className="flex items-center gap-2 text-xs">
                  {it.success ? (
                    <span className="text-green-400 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Sendt (Resend ID: {it.resend_id || "—"})
                    </span>
                  ) : (
                    <span className="text-red-400 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Feilet
                    </span>
                  )}
                </div>
                {it.error && (
                  <div className="mt-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 p-2">
                    {it.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="text-[10px] text-[var(--cream-faint)] italic pt-4 border-t border-white/5">
          Tips: Feilmeldinger inneholder Resend-status. «You do not have permission to send email to unverified recipients» = domenet er i sandbox-modus. «Rate limit» = for mange forespørsler samtidig. Tom liste = ingen forsøk logget.
        </div>
      </div>
    </Layout>
  );
}
